import * as React from 'react';
import { HigloLogo } from './illustrations';
import { cn } from '../../lib/utils';

/* Page shells for v9.

   Desktop strategy (per product decision): keep the mobile-first phone column
   on all sizes — we simply cap the inner column a bit wider than a phone
   (max-w-xl / 576px) on md+ so the layout doesn't float in whitespace. The
   slate page background behind the white inner card makes desktop feel
   intentional, like a centred "app frame". On mobile we fill full viewport
   width — no margin, no rounded outer frame — so every phone width renders
   edge-to-edge.
   Padding inside shells matches the v9 mockup — the mockup starts every
   screen with a 32px "status-bar" spacer (`h-8`) then content. We bake that
   directly into the shells. */

const FRAME_WIDTH = 'max-w-[420px] md:max-w-[520px]';

export function ScreenFrame({ className, children }) {
  return (
    <div className="min-h-screen w-full bg-slate-50 flex justify-center">
      <div
        className={cn(
          'w-full min-h-screen bg-white flex flex-col',
          FRAME_WIDTH,
          'md:min-h-0 md:my-6 md:rounded-[28px] md:shadow-[0_25px_60px_-30px_rgba(15,23,42,0.35)] md:ring-1 md:ring-slate-200/70 md:overflow-hidden',
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
}

/* AccountPortalShell — small logo top-left, "ACCOUNT PORTAL" caps top-right.
   Wraps any content (which provides its own padding). Matches v9 spacing
   (`px-5 pt-3 pb-2` on the header row, right after the 32px status spacer). */
export function AccountPortalShell({ children, rightSlot, className }) {
  return (
    <ScreenFrame className={className}>
      <div className="h-6 flex-shrink-0" />
      <div className="px-4 pb-1 flex items-center justify-between">
        <HigloLogo className="w-6 h-6" />
        {rightSlot !== undefined ? (
          rightSlot
        ) : (
          <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-slate-500">
            Account Portal
          </div>
        )}
      </div>
      <div className="flex-1 min-h-0">{children}</div>
    </ScreenFrame>
  );
}

/* CenteredWelcomeShell — sign-in / passcode / other focused pre-auth screens.
   Matches v9 mockup 1.1 / 1.2 spacing: 32px status spacer, then 40px top
   padding, logo, 8px label, 20px title spacing, subtitle.

   `titleSize` accepts either a number (raw px, legacy) or one of
   'sm'|'md'|'lg'|'xl'|'2xl'|'3xl' matching the SerifHeading sizes:
     sm=20 md=22 lg=24 xl=26 2xl=28 3xl=32. Default '2xl' (28) which is what
     1.1 SignIn wants; 1.2 Verify wants 'md' (22). */
const TITLE_SIZE_MAP = { sm: 20, md: 22, lg: 24, xl: 26, '2xl': 28, '3xl': 32 };

export function CenteredWelcomeShell({ title, subtitle, titleSize = '2xl', children, className }) {
  const px =
    typeof titleSize === 'number' ? titleSize : TITLE_SIZE_MAP[titleSize] || 28;
  return (
    <ScreenFrame className={className}>
      <div className="h-8 flex-shrink-0" />
      <div className="px-6 pt-8 pb-6 flex flex-col items-center">
        <HigloLogo className="w-10 h-10" />
        <div className="mt-2 text-[10px] font-bold tracking-[0.22em] uppercase text-slate-500">
          Account Portal
        </div>
        {title && (
          <h1
            className="font-heading font-medium text-slate-900 leading-[1.15] tracking-[-0.015em] mt-5 mb-2 text-center"
            style={{ fontSize: px }}
          >
            {title}
          </h1>
        )}
        {subtitle && (
          <p className="text-[13px] text-slate-500 text-center max-w-[300px] leading-relaxed mb-6">
            {subtitle}
          </p>
        )}
        <div className="w-full">{children}</div>
      </div>
    </ScreenFrame>
  );
}

/* StageShell — used by the AccountSetup wizard and any screen that has a
   "Stage 1 of 2" header strip. */
export function StageShell({
  stageNumber,
  stageLabel,
  stageTitle,
  status = 'active',
  rightSlot,
  children,
  className,
}) {
  const dotBg = status === 'done' ? 'bg-emerald-500' : 'bg-slate-900';
  const labelClass = status === 'done' ? 'text-emerald-700' : 'text-slate-500';
  const checkIcon = (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
  // When no right-slot content is supplied, centre the logo (drops the 3-cell
  // flex that otherwise renders an empty slot on the right).
  const headerJustify = rightSlot ? 'justify-between' : 'justify-center';
  return (
    <ScreenFrame className={className}>
      <div className="h-8 flex-shrink-0" />
      <div className={cn('px-5 pb-1 flex items-center min-h-[28px]', headerJustify)}>
        {rightSlot ? <span /> : null}
        <HigloLogo className="w-5 h-5" />
        {rightSlot ? (
          <div className="flex justify-end min-w-[60px]">{rightSlot}</div>
        ) : null}
      </div>
      {stageNumber != null && (
        <>
          <div className="px-5 pt-3 pb-3 flex items-center gap-3">
            <div className={cn('w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-semibold text-white', dotBg)}>
              {status === 'done' ? checkIcon : stageNumber}
            </div>
            <div className="flex-1">
              <div className={cn('text-[10px] font-bold tracking-[0.14em] uppercase', labelClass)}>
                {stageLabel}
              </div>
              <div className="text-[13px] font-semibold text-slate-900">{stageTitle}</div>
            </div>
          </div>
          <div className="h-px mx-4 mb-4 bg-slate-200" />
        </>
      )}
      <div className="flex-1 min-h-0">{children}</div>
    </ScreenFrame>
  );
}

/* DetailShell — back-button row + breadcrumb header for drill-in pages. */
export function DetailShell({ onBack, breadcrumb, rightSlot, children, className, backIcon = 'chevron' }) {
  return (
    <ScreenFrame className={className}>
      <div className="h-8 flex-shrink-0" />
      <div className="px-2 py-2 flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          className="h-9 w-9 rounded-full inline-flex items-center justify-center text-slate-700 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-1 transition-colors"
        >
          {backIcon === 'close' ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          )}
        </button>
        {breadcrumb && (
          <div className="text-[13px] text-slate-500">{breadcrumb}</div>
        )}
        <div className="flex-1" />
        {rightSlot}
      </div>
      <div className="flex-1 min-h-0">{children}</div>
    </ScreenFrame>
  );
}

/* TabbedShell — post-auth chrome. Title row + shadcn-style tab strip. */
export function TabbedShell({ title, rightSlot, children, className }) {
  return (
    <ScreenFrame className={className}>
      <div className="h-6 flex-shrink-0" />
      <div className="px-4 pt-1 pb-1 flex items-center justify-between">
        <h1 className="text-[20px] font-bold tracking-tight text-slate-900">{title}</h1>
        <div className="flex items-center gap-2">
          {rightSlot}
          <HigloLogo className="w-5 h-5" />
        </div>
      </div>
      <div className="flex-1 min-h-0">{children}</div>
    </ScreenFrame>
  );
}
